/**
 * The wire protocol of the realtime socket (ADR 0029).
 *
 * The client only ever subscribes, unsubscribes, reports its presence and pings; every
 * other write goes through HTTP. The server answers with envelopes `{type, topic, data, at}`
 * whose `data` is the same shape the matching HTTP endpoint returns, so no frame can show
 * more than the API would.
 */
import type { AttemptView } from "@/core/play/api";
import type { ChatMessage } from "@/core/chat/api";

export type ClientFrame =
  | { type: "subscribe"; topic: string }
  | { type: "unsubscribe"; topic: string }
  | { type: "presence"; topic: string; state: unknown }
  /** A realtime duel's frame, the one write the socket takes (ADR 0077). */
  | { type: "game"; topic: string; kind: string; data: unknown }
  | { type: "ping" };

export interface Envelope<T extends string, D> {
  type: T;
  topic: string;
  data: D;
  at: string;
}

/** A teammate present on a shared board; `state` is whatever their Player reported. */
export interface PresencePeer<S = unknown> {
  user_id: number;
  name: string;
  state: S | null;
  at: string;
}

export type ErrorCode =
  | "unauthorized"
  | "bad_frame"
  | "presence_unsupported"
  | "presence_too_large"
  | "game_unsupported"
  | "frame_too_large";

export type ServerFrame =
  | Envelope<"subscribed", Record<string, never>>
  | Envelope<"error", { code: ErrorCode }>
  | Envelope<"topic.revoked", { reason: string }>
  | Envelope<
      "attempt.changed",
      {
        reason: "progress" | "submission" | "solved";
        attempt: AttemptView;
        progress_state: unknown;
      }
    >
  /** A teammate replayed the board: this run is over, the named one replaced it (ADR 0037). */
  | Envelope<"attempt.restarted", { attempt_id: number }>
  | Envelope<"presence.snapshot", { peers: PresencePeer[] }>
  | Envelope<"presence.update", PresencePeer>
  | Envelope<"presence.leave", { user_id: number }>
  | Envelope<"leaderboard.changed", { slug: string }>
  /** Somebody entered a leaderboard: the puzzle list's feed refetches (ADR 0043). */
  | Envelope<"activity.changed", { slug: string }>
  /** How many people are on this puzzle right now, the sender included (ADR 0038). */
  | Envelope<"solvers.count", { count: number }>
  | Envelope<"team.changed", { team_id: number; reason: string }>
  | Envelope<"chat.message", ChatMessage>
  /** Something changed on a duel: refetch it (ADR 0076). */
  | Envelope<"duel.changed", { duel_id: number; status?: string }>
  /** A challenge or a turn came or went for this user, or in the lobby. */
  | Envelope<"duels.changed", { duel_id: number; status: string }>
  /** The opponent's realtime frame, relayed (ADR 0077). */
  | Envelope<"duel.frame", { side: "white" | "black"; kind: string; data: unknown }>
  | { type: "pong"; at: string };

/** Close codes the server uses to say "do not retry". */
export const CLOSE_UNAUTHENTICATED = 4401;
export const CLOSE_UNKNOWN_TENANT = 4404;

export const attemptTopic = (id: number): string => `attempt:${id}`;
export const leaderboardTopic = (slug: string): string => `leaderboard:${slug}`;
/** The one tenant-wide topic: a solve on any puzzle, for the recent-solves feed. */
export const ACTIVITY_TOPIC = "activity";
export const teamTopic = (id: number): string => `team:${id}`;
/** The head count of a puzzle: open to anyone, since it carries a number and no name. */
export const solversTopic = (slug: string): string => `solvers:${slug}`;
/** "I am one of them": subscribing is the declaration, unsubscribing takes it back. */
export const solvingTopic = (slug: string): string => `solving:${slug}`;
